/**
 * TeleTime POS - Update Banner (Piece B)
 *
 * Small, passive, tap-to-apply pill shown when a new SW is installed and
 * waiting. Nothing auto-activates — the user taps; applyUpdate() messages the
 * SW (SKIP_WAITING) and Piece A's controllerchange handler performs the one
 * guarded reload.
 *
 * Mounted from main.jsx OUTSIDE App's conditional returns (like BuildStamp),
 * so it is visible on device-init, PIN-lock, and the main POS alike — the
 * lock screen is where staff can most safely tap it (and where Piece C will
 * auto-apply).
 *
 * Mid-transaction call: we cannot see POSMain's checkout-modal state from
 * this mount point (local useState there), so the proxy is the persisted
 * cart: NON-EMPTY cart → inline two-step confirm ("Cart in progress —
 * confirm"); empty cart → apply on first tap. The cart itself survives the
 * reload (unified-store persists to IndexedDB), so the confirm guards the
 * genuinely risky window (an in-flight checkout implies a non-empty cart)
 * without a blocking native dialog.
 *
 * Dismiss is temporary by design: the update is still waiting, so the pill
 * returns after BANNER_REAPPEAR_MS.
 */

import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { getSwUpdater } from '../swUpdater';
import { useUnifiedStore } from '../store/unifiedStore';

export const BANNER_REAPPEAR_MS = 5 * 60_000;

export function UpdateBanner({ updater = getSwUpdater() }) {
  const [waiting, setWaiting] = useState(() => !!updater.getWaitingWorker());
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  const cartItemCount = useUnifiedStore((s) => s.items.length);

  useEffect(() => updater.onUpdateWaiting(() => setWaiting(true)), [updater]);

  // Dismiss is temporary — the update is still waiting; resurface later.
  useEffect(() => {
    if (!dismissed) return;
    const t = setTimeout(() => setDismissed(false), BANNER_REAPPEAR_MS);
    return () => clearTimeout(t);
  }, [dismissed]);

  if (!waiting || dismissed) return null;

  const handleApply = () => {
    if (applying) return;
    if (cartItemCount > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    setApplying(true);
    updater.applyUpdate();
    // Piece A's controllerchange handler reloads once the new SW activates.
  };

  return (
    <div
      data-testid="pos-update-banner"
      className="fixed bottom-20 left-3 z-[100] flex items-center gap-2 rounded-full bg-blue-600 text-white shadow-lg pl-3 pr-1 py-1 text-xs"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${applying ? 'animate-spin' : ''}`} />
      {applying ? (
        <span className="pr-2">Updating&hellip;</span>
      ) : (
        <>
          <span>{confirming ? 'Cart in progress — apply update now?' : 'Update ready'}</span>
          <button
            onClick={handleApply}
            className="rounded-full bg-white/20 hover:bg-white/30 px-2.5 py-0.5 font-semibold"
          >
            {confirming ? 'Confirm' : 'Apply'}
          </button>
          <button
            aria-label="Dismiss"
            onClick={() => { setDismissed(true); setConfirming(false); }}
            className="rounded-full hover:bg-white/20 p-1"
          >
            <X className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}

export default UpdateBanner;
